<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-semibold">My Movie Collection</h2>
      <Button variant="outline" size="sm" :disabled="isSharingSet" @click="handleShareSet">
        <Icon v-if="!isSharingSet" name="heroicons:share" class="h-4 w-4 mr-1.5" />
        <Icon v-else name="heroicons:arrow-path" class="h-4 w-4 mr-1.5 animate-spin" />
        {{ isSharingSet ? 'Creating Link...' : 'Share Movie Set' }}
      </Button>
    </div>

    <!-- Search -->
    <div class="mb-6">
      <FilterInput v-model="searchQuery" placeholder="Search movies by title..." />
    </div>

    <!-- Random Suggestion -->
    <div class="mb-8">
      <h3 class="text-xl font-semibold mb-4 text-center">Random Movie Suggestion</h3>
      <RandomMovie
        :movie="movieStore.randomMovie"
        @refresh="movieStore.getRandomMovie"
        @update:bubble="handleBubbleUpdate"
      />
      <Separator class="my-6" />
    </div>

    <!-- Filters Section -->
    <ClientOnly>
      <Collapsible v-model:open="isFiltersOpen" class="mb-8 border rounded-lg p-4">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-semibold">Filter Movies</h3>
          <CollapsibleTrigger as-child>
            <Button variant="ghost" size="sm" class="w-9 p-0">
              <Icon
                :name="isFiltersOpen ? 'lucide:chevrons-up-down' : 'lucide:chevrons-down-up'"
                class="h-4 w-4"
              />
              <span class="sr-only">Toggle Filters</span>
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- Genre Filter -->
            <div>
              <h4 class="text-sm font-medium mb-2">Genre</h4>
              <TypeSelector
                v-model="selectedGenre"
                :types="movieStore.uniqueGenres"
                placeholder="Select Genre"
              />
            </div>
            <!-- Actor Filter -->
            <div>
              <h4 class="text-sm font-medium mb-2">Actor</h4>
              <TypeSelector
                v-model="selectedActor"
                :types="movieStore.uniqueActors"
                placeholder="Select Actor"
              />
            </div>
            <!-- Director Filter -->
            <div>
              <h4 class="text-sm font-medium mb-2">Director</h4>
              <TypeSelector
                v-model="selectedDirector"
                :types="movieStore.uniqueDirectors"
                placeholder="Select Director"
              />
            </div>
            <!-- Watched Status Filter -->
            <div>
              <h4 class="text-sm font-medium mb-2">Watched Status</h4>
              <Select v-model="selectedWatchedStatus">
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Status</SelectItem>
                  <SelectItem value="Watched">Watched</SelectItem>
                  <SelectItem value="Unwatched">Unwatched</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <!-- Year Filter -->
            <div class="md:col-span-2 lg:col-span-1">
              <h4 class="text-sm font-medium mb-2">Release Year</h4>
              <div class="flex items-center gap-2">
                <Input
                  v-model="minYearInput"
                  type="number"
                  placeholder="Min Year"
                  class="flex-1"
                  :min="movieStore.publicationYearRange.min ?? undefined"
                  :max="movieStore.publicationYearRange.max ?? undefined"
                  @blur="applyYearFilters"
                />
                <span>-</span>
                <Input
                  v-model="maxYearInput"
                  type="number"
                  placeholder="Max Year"
                  class="flex-1"
                  :min="movieStore.publicationYearRange.min ?? undefined"
                  :max="movieStore.publicationYearRange.max ?? undefined"
                  @blur="applyYearFilters"
                />
              </div>
              <p
                v-if="movieStore.publicationYearRange.min !== null"
                class="text-xs text-muted-foreground mt-1"
              >
                Available range: {{ movieStore.publicationYearRange.min }} -
                {{ movieStore.publicationYearRange.max }}
              </p>
            </div>
          </div>
          <div class="flex justify-end border-t pt-4 mt-4">
            <Button variant="ghost" @click="movieStore.resetFilters()">Reset All Filters</Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <template #fallback>
        <div class="mb-8 border rounded-lg p-4 animate-pulse">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-semibold h-6 bg-muted rounded w-1/3"></h3>
            <div class="h-9 w-9 bg-muted rounded"></div>
          </div>
        </div>
      </template>
    </ClientOnly>

    <!-- Movie List Section -->
    <ClientOnly>
      <!-- Loading State -->
      <div
        v-if="bubblesStore.loading && !movieStore.filteredMovies.length"
        class="text-center py-8"
      >
        <p class="text-muted-foreground">Loading movies...</p>
        <!-- Spinner? -->
      </div>
      <!-- Error State -->
      <div v-else-if="bubblesStore.error" class="text-center py-8 text-destructive">
        <p>{{ bubblesStore.error }}</p>
      </div>
      <!-- Empty State (No Movies Added Yet) -->
      <div
        v-else-if="!bubblesStore.loading && movieStore.movies.length === 0"
        class="text-center py-8"
      >
        <p class="text-muted-foreground mb-4">You don't have any movies yet.</p>
        <Button @click="navigateToAddMovie">Add Your First Movie</Button>
      </div>
      <!-- Empty State (Filters Applied) -->
      <div
        v-else-if="!bubblesStore.loading && movieStore.filteredMovies.length === 0"
        class="text-center py-8"
      >
        <p class="text-muted-foreground mb-4">No movies match your current filters.</p>
        <Button variant="ghost" @click="movieStore.resetFilters()">Clear Filters</Button>
      </div>
      <!-- Movie Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <AppLink
          v-for="movie in movieStore.filteredMovies"
          :key="movie.uuid"
          :to="`/bubble/${movie.uuid}`"
        >
          <div
            class="border rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer bg-card h-full flex flex-col min-h-[200px]"
          >
            <!-- Image & Basic Info -->
            <div class="flex items-start mb-3 flex-grow">
              <!-- Image -->
              <div
                v-if="movie.meta?.offlineMediaResources?.cover_photo || movie.poster"
                class="w-1/3 pr-4 flex-shrink-0"
              >
                <NuxtImg
                  :src="movie.meta?.offlineMediaResources?.cover_photo || movie.poster || undefined"
                  :alt="movie.title"
                  class="w-full h-auto object-cover rounded aspect-[2/3]"
                  loading="lazy"
                  width="150"
                  :height="225"
                />
              </div>
              <!-- Core Details -->
              <div class="flex-grow min-w-0">
                <h3 class="text-base font-semibold mb-1 truncate" :title="movie.title">
                  {{ movie.title }}
                </h3>
                <p
                  class="text-sm text-muted-foreground truncate"
                  :title="movie.director?.join(', ')"
                >
                  Dir: {{ movie.director?.join(', ') || 'N/A' }}
                </p>
                <p class="text-sm text-muted-foreground truncate" :title="getActorsDisplay(movie)">
                  Cast: {{ getActorsDisplay(movie) }}
                </p>
                <p class="text-sm text-muted-foreground">{{ movie.year }}</p>
                <p v-if="movie.runtime" class="text-xs text-muted-foreground">
                  {{ movie.runtime }} min
                </p>
              </div>
            </div>
            <!-- Footer: Badges/Rating -->
            <div
              class="mt-auto pt-2 flex flex-wrap items-center justify-between gap-1 border-t border-border/50"
            >
              <div class="flex flex-wrap gap-1">
                <Badge v-if="movie.watched" variant="secondary" class="text-xs font-normal"
                  >Watched</Badge
                >
                <Badge v-else variant="outline" class="text-xs font-normal">Unwatched</Badge>
                <span
                  v-for="genre in getGenres(movie).slice(0, 2)"
                  :key="genre"
                  class="text-xs bg-muted text-foreground px-1.5 py-0.5 rounded-full truncate"
                  :title="genre"
                >
                  {{ genre }}
                </span>
                <span v-if="getGenres(movie).length > 2" class="text-xs text-muted-foreground"
                  >+{{ getGenres(movie).length - 2 }}</span
                >
              </div>
              <div class="text-right">
                <p v-if="movie.imdbRating" class="text-sm font-medium">{{ movie.imdbRating }}/10</p>
                <p v-else-if="movie.userRating" class="text-sm font-medium">
                  {{ movie.userRating }}/5 <span class="text-xs">(user)</span>
                </p>
              </div>
            </div>
          </div>
        </AppLink>
      </div>
      <!-- Fallback for Movie List ClientOnly -->
      <template #fallback>
        <div class="text-center py-8">
          <p class="text-muted-foreground">Loading movies...</p>
          <!-- Grid skeleton loader could go here -->
        </div>
      </template>
    </ClientOnly>

    <!-- No Load More button for now, assuming all are fetched -->
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch, ref, h } from 'vue'
import { useBubblesStore } from '~/stores/bubbles'
import { useMovieStore } from '~/stores/core/movieStore'
import RandomMovie from '~/components/bubbles/view/helpers/RandomMovie.vue'
import FilterInput from '~/components/bubbles/view/helpers/FilterInput.vue'
import TypeSelector from '~/components/bubbles/view/helpers/TypeSelector.vue'
import { useRouter } from 'vue-router'
import { Button } from '@/components/ui/button'
import { useNavigationStore } from '@/stores/navigationStore'
import type { MovieBubble } from '~/stores/interfaces'
import AppLink from '@/components/ui/link/AppLink.vue'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { logError } from '@/composables/useLogger'
import { useToast } from '@/components/ui/toast'
import CopyButton from '@/components/ui/button/CopyButton.vue'
import { useUserShareStore } from '~/stores/userShare'
import { useClipboard } from '@vueuse/core'

const router = useRouter()
const bubblesStore = useBubblesStore()
const movieStore = useMovieStore()
const navigationStore = useNavigationStore()
const userShareStore = useUserShareStore()
const { toast } = useToast()
const { copy, copied, isSupported } = useClipboard()

const isFiltersOpen = ref(false)
const isSharingSet = ref(false)

// --- Filter Computed Properties ---
const searchQuery = computed({
  get: () => movieStore.searchQuery,
  set: (value) => movieStore.setSearchQuery(value),
})

const selectedActor = computed({
  get: () => movieStore.selectedActor,
  set: (value) => movieStore.setFilters({ actor: value }),
})

const selectedGenre = computed({
  get: () => movieStore.selectedGenre,
  set: (value) => movieStore.setFilters({ genre: value }),
})

const selectedDirector = computed({
  get: () => movieStore.selectedDirector,
  set: (value) => movieStore.setFilters({ director: value }),
})

const selectedWatchedStatus = computed({
  get: () => movieStore.selectedWatchedStatus,
  set: (value: 'All' | 'Watched' | 'Unwatched') => movieStore.setFilters({ watchedStatus: value }),
})

// Local refs for year inputs
const minYearInput = ref<string>(movieStore.minYear?.toString() ?? '')
const maxYearInput = ref<string>(movieStore.maxYear?.toString() ?? '')

function applyYearFilters() {
  const minYear = minYearInput.value ? parseInt(minYearInput.value, 10) : null
  const maxYear = maxYearInput.value ? parseInt(maxYearInput.value, 10) : null
  if ((minYear !== null && isNaN(minYear)) || (maxYear !== null && isNaN(maxYear))) return
  movieStore.setFilters({ minYear, maxYear })
}

watch(
  () => [movieStore.minYear, movieStore.maxYear],
  ([min, max]) => {
    minYearInput.value = min?.toString() ?? ''
    maxYearInput.value = max?.toString() ?? ''
  }
)
// --- End Filter Computed Properties ---

onMounted(async () => {
  movieStore.resetFilters() // Reset filters on mount
  try {
    if (!bubblesStore.bubbleTypes['movie']) {
      await bubblesStore.fetchBubbleTypes()
    }
    await bubblesStore.fetchBubblesByType('movie')
    movieStore.setMovies((bubblesStore.bubblesByType['movie'] || []) as MovieBubble[])
  } catch (err) {
    logError('MovieTypeView: Failed to fetch movies:', err)
    // Maybe set an error state in movieStore?
  }
})

function navigateToAddMovie() {
  router.push('/dashboard/add')
}

const handleBubbleUpdate = (updatedBubble: MovieBubble) => {
  movieStore.updateMovie(updatedBubble)
}

watch(
  () => movieStore.filteredMovies,
  (movies) => {
    navigationStore.setCurrentType('movie')
    navigationStore.setCurrentTypeItems(movies.map((movie) => movie.uuid))
  },
  { immediate: true, deep: true } // Deep might be needed if filters depend on nested props
)

// Watch for filter changes to reset pagination (if we add it later)
// watch(
//   [searchQuery, selectedActor, selectedGenre, selectedDirector, selectedWatchedStatus, () => movieStore.minYear, () => movieStore.maxYear],
//   () => {
//     // Reset pagination if added
//   },
//   { deep: true }
// )

// Function to get genres (already defined in original script, keep it)
const getGenres = (movie: MovieBubble): string[] => {
  if (!movie.genre) return []
  if (Array.isArray(movie.genre)) return movie.genre
  // Handle potential non-string genres safely
  return typeof movie.genre === 'string' ? movie.genre.split(',').map((g: string) => g.trim()) : []
}

// Function to get actors display string (already defined in original script, keep it)
const getActorsDisplay = (movie: MovieBubble): string => {
  if (!movie.actors || movie.actors.length === 0) return 'N/A'
  return movie.actors.length > 2
    ? `${movie.actors[0]}, ${movie.actors[1]}, ...`
    : movie.actors.join(', ')
}

// --- Share Set Logic ---
const handleShareSet = async () => {
  isSharingSet.value = true
  try {
    const shareData = {
      shareType: 'set' as const,
      targetBubbleType: 'movie', // Type for this view
      isPublic: true,
    }
    const result = await userShareStore.createShareLink(shareData)

    if (result && result.url) {
      toast({
        title: 'Share Link Created',
        description: 'Your set of movies can be shared via this link.',
        duration: 9000,
        action: h(CopyButton, { textToCopy: result.url, variant: 'secondary' }),
      })
      if (isSupported.value) {
        await copy(result.url)
        if (copied.value) {
          toast({
            title: 'Link Copied!',
            description: 'The share link has been copied to your clipboard.',
            duration: 3000,
          })
        } else {
          toast({
            title: 'Copy Failed',
            description:
              'Could not automatically copy the link. Please use the button in the previous message.',
            variant: 'destructive',
            duration: 5000,
          })
        }
      }
    } else {
      if (!result) {
        toast({
          title: 'Error',
          description: 'Could not create share link for set.',
          variant: 'destructive',
        })
      }
      // Store handles other errors
    }
  } catch (error) {
    logError('Error creating movie set share link:', error)
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while sharing the set.',
      variant: 'destructive',
    })
  } finally {
    isSharingSet.value = false
  }
}
</script>
